<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import KaptionNewConversationModal from './KaptionNewConversationModal.vue';

const props = defineProps({
  // Placement-specific styling:
  //  - 'labeled'  : full green pill with label (generic / top bar)
  //  - 'compact'  : round green icon button (compact rail — KaptionSidebarV2)
  //  - 'row'      : green icon button sized to a search-row (KaptionSidebar)
  variant: {
    type: String,
    default: 'labeled',
    validator: value => ['labeled', 'compact', 'row'].includes(value),
  },
});

const { t } = useI18n();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();

const isEnabled = computed(() =>
  isKaptionFlagEnabled('KAPTION_NEW_CONVERSATION_MODAL')
);

const buttonClass = computed(() => {
  const base =
    'flex items-center justify-center bg-n-brand text-white shadow-sm transition-[filter,background-color] hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand/50';
  switch (props.variant) {
    case 'compact':
      return `${base} size-10 rounded-full`;
    case 'row':
      return `${base} shrink-0 h-7 px-2.5 rounded-2xl`;
    default:
      return `${base} h-8 gap-1.5 px-3 text-sm font-medium rounded-lg`;
  }
});

const iconSize = computed(() =>
  props.variant === 'labeled' ? 'size-4' : 'size-[1.15rem]'
);

const isOpen = ref(false);
const open = () => {
  isOpen.value = true;
};
const close = () => {
  isOpen.value = false;
};
</script>

<template>
  <button
    v-if="isEnabled"
    v-tooltip.right="
      variant !== 'labeled'
        ? t('NEW_CONVERSATION_MODAL.NEW_MESSAGE')
        : undefined
    "
    type="button"
    data-testid="new-message-button"
    :aria-label="t('NEW_CONVERSATION_MODAL.NEW_MESSAGE')"
    :class="buttonClass"
    @click="open"
  >
    <span class="i-lucide-message-square-plus" :class="iconSize" />
    <span v-if="variant === 'labeled'">
      {{ t('NEW_CONVERSATION_MODAL.NEW_MESSAGE') }}
    </span>
  </button>
  <Teleport to="body">
    <KaptionNewConversationModal v-if="isEnabled && isOpen" @close="close" />
  </Teleport>
</template>
